import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink } from 'lucide-react';
import MarketingHeader from '@/components/MarketingHeader';
import MarketingFooter from '@/components/MarketingFooter';
import SEO from '@/components/SEO';

const projects = [
  {
    id: 1,
    name: 'EmotionalOS',
    tagline: 'AI-Powered Emotional Intelligence Platform',
    description: 'A revolutionary platform that helps users understand and manage their emotional well-being through AI-driven insights and personalized recommendations.',
    image: 'https://customer-assets.emergentagent.com/job_fearless-saas-1/artifacts/g6v6qty8_image.png',
    url: 'https://emotionalos.app',
    tags: ['AI/ML', 'Healthcare', 'Mobile App'],
    industry: 'Healthcare',
    platform: ['AI/ML', 'Mobile App'],
    gradient: 'from-purple-500 via-pink-500 to-orange-500'
  },
  {
    id: 2,
    name: 'LaborTrace',
    tagline: 'Construction Workforce Management Platform',
    description: 'A powerful labor management solution for the construction industry. Track workforce hours, manage crews, monitor job site productivity, and streamline payroll with real-time visibility.',
    image: 'https://customer-assets.emergentagent.com/job_project-wizard-val/artifacts/qff3krrt_LaborTrace_FearlessIT.png',
    url: 'https://labortrace.io',
    tags: ['Construction', 'Workforce', 'SaaS'],
    industry: 'Construction',
    platform: ['SaaS', 'Web Platform'],
    gradient: 'from-amber-500 via-orange-500 to-red-500'
  },
  {
    id: 3,
    name: 'StockSignal',
    tagline: 'Real-Time Stock Market Analytics',
    description: 'A powerful stock market analytics platform providing real-time signals, technical analysis, and market insights to help traders make informed investment decisions.',
    image: 'https://customer-assets.emergentagent.com/job_project-wizard-val/artifacts/a6x7n7ze_StockSignal_FearlessIT.png',
    url: 'https://stocksignal.fearlessit.io',
    tags: ['FinTech', 'Analytics', 'SaaS'],
    industry: 'FinTech',
    platform: ['SaaS', 'Web Platform'],
    gradient: 'from-emerald-500 via-teal-500 to-cyan-500'
  },
  {
    id: 4,
    name: 'FilmFuse',
    tagline: 'Smart Film Discovery & Recommendations',
    description: 'An intelligent film discovery platform that uses advanced algorithms to match viewers with their perfect movies based on mood, preferences, and viewing history.',
    image: 'https://customer-assets.emergentagent.com/job_fearless-saas-1/artifacts/yzbes63a_image.png',
    url: 'https://filmfuseapp.com/',
    tags: ['Entertainment', 'AI/ML', 'Web Platform'],
    industry: 'Entertainment',
    platform: ['AI/ML', 'Web Platform'],
    gradient: 'from-cyan-500 via-blue-500 to-purple-500'
  },
  {
    id: 5,
    name: 'DeShazer Foundation',
    tagline: 'Nonprofit Organization Website',
    description: 'A modern, impactful website for the DeShazer Foundation, showcasing their mission to serve communities and inspire positive change through charitable initiatives.',
    image: 'https://customer-assets.emergentagent.com/job_5f14270e-5f8e-49f2-b49c-b70dead36efd/artifacts/08g1vt6e_image.png',
    url: 'https://deshazerfoundation.org/',
    tags: ['Non-Profit', 'Web Design', 'SaaS Platform'],
    industry: ['Non-Profit', 'Web Design'],
    platform: ['Web Platform', 'SaaS'],
    gradient: 'from-teal-400 via-emerald-500 to-green-600'
  },
  {
    id: 6,
    name: 'ReWord',
    tagline: 'Multilingual Property Management Software',
    description: 'Helping landlords and tenants communicate even if they do not speak the same language. Effortless, intuitive, time-saving software for property management.',
    image: 'https://customer-assets.emergentagent.com/job_f638f114-9111-40a2-b23c-9828d06c5dac/artifacts/j78qwmwq_image.png',
    url: 'https://portal.fearlessit.com/preview/2ade5b17?t=1769550394637',
    tags: ['Real Estate', 'SaaS', 'Multilingual'],
    industry: 'Real Estate',
    platform: ['SaaS', 'Web Platform'],
    gradient: 'from-green-400 via-lime-500 to-green-600'
  },
  {
    id: 7,
    name: 'My Local Lead Flow',
    tagline: 'Lead Generation & Review Platform',
    description: 'Get the stars and leads you deserve without buying ads and without wasting time on social media. A powerful lead generation system for local businesses.',
    image: 'https://customer-assets.emergentagent.com/job_f638f114-9111-40a2-b23c-9828d06c5dac/artifacts/g5xafxd1_image.png',
    url: 'https://portal.fearlessit.com/preview/c7fb7f56',
    tags: ['Marketing', 'Lead Generation', 'SaaS'],
    industry: 'Marketing',
    platform: ['SaaS', 'Web Platform'],
    gradient: 'from-blue-400 via-indigo-500 to-purple-500'
  },
  {
    id: 8,
    name: 'B.Combs',
    tagline: 'Automation & Data Management Platform',
    description: 'The automation needed to enhance your mission. Track metrics, aggregate data, streamline communication, and standardize data collection across your organization.',
    image: 'https://customer-assets.emergentagent.com/job_f638f114-9111-40a2-b23c-9828d06c5dac/artifacts/lil3r8hk_image.png',
    url: 'https://portal.fearlessit.com/preview/da53c60f?t=1769550732925',
    tags: ['Automation', 'Data Management', 'Enterprise'],
    industry: 'Enterprise',
    platform: ['SaaS', 'Web Platform', 'Mobile App'],
    gradient: 'from-orange-400 via-amber-500 to-yellow-500'
  },
  {
    id: 9,
    name: 'Express Websites',
    tagline: 'Quick-Launch Website Templates',
    description: 'Professional website templates for businesses, restaurants, portfolios, stores, and more. Get online fast with beautiful, responsive designs ready to customize.',
    image: 'https://customer-assets.emergentagent.com/job_f638f114-9111-40a2-b23c-9828d06c5dac/artifacts/ce354ewv_image.png',
    url: 'https://www.fearlessit.com/duda-templates-express-websites',
    tags: ['Web Design', 'Templates', 'Business'],
    industry: 'Web Design',
    platform: ['Web Platform'],
    gradient: 'from-indigo-500 via-purple-500 to-violet-500'
  },
  {
    id: 10,
    name: 'PhishSignal',
    tagline: 'Cybersecurity Awareness & Phishing Detection',
    description: 'A comprehensive cybersecurity platform that helps organizations detect, prevent, and respond to phishing attacks through advanced threat intelligence and employee training.',
    image: 'https://customer-assets.emergentagent.com/job_44963c42-dfcc-45e4-a138-fc34a746e401/artifacts/tzx9j78q_image.png',
    url: 'https://phishsignal.com/',
    tags: ['Cybersecurity', 'Security', 'Web App'],
    industry: 'Cybersecurity',
    platform: ['Web Platform', 'SaaS'],
    gradient: 'from-red-500 via-orange-500 to-yellow-500'
  }
];

const industries = ['Healthcare', 'Construction', 'FinTech', 'Entertainment', 'Real Estate', 'Marketing', 'Enterprise', 'Web Design', 'Non-Profit', 'Cybersecurity'];
const platforms = ['AI/ML', 'Mobile App', 'Web Platform', 'SaaS'];

const ProjectCard = ({ project }) => {
  return (
    <div className="group relative" data-testid={`project-card-${project.id}`}>
      {/* Animated gradient border */}
      <div 
        className={`absolute -inset-[2px] bg-gradient-to-r ${project.gradient} rounded-2xl opacity-50 group-hover:opacity-100 blur-sm group-hover:blur-none transition-all duration-500 animate-gradient-shift`}
      />
      <div 
        className={`absolute -inset-[2px] bg-gradient-to-r ${project.gradient} rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
        style={{ animation: 'gradient-rotate 3s linear infinite' }}
      />
      
      {/* Card content */}
      <div className="relative bg-white rounded-2xl overflow-hidden shadow-lg group-hover:shadow-2xl transition-all duration-500">
        {/* Project image */}
        <div className="relative h-[250px] sm:h-[350px] md:h-[400px] overflow-hidden">
          <img 
            src={project.image} 
            alt={project.name}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          
          {/* Hover overlay with link */}
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
            <a 
              href={project.url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-white/90 backdrop-blur-sm text-carbon-black px-4 sm:px-6 py-2 sm:py-3 rounded-full font-semibold flex items-center space-x-2 hover:bg-white transition-colors shadow-lg text-sm sm:text-base"
            >
              <span>View Project</span>
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>
        
        {/* Project info */}
        <div className="p-5 sm:p-8">
          <div className="flex flex-wrap gap-2 mb-3 sm:mb-4">
            {project.tags.map((tag, index) => (
              <span 
                key={index}
                className="px-2 sm:px-3 py-1 bg-gradient-to-r from-gray-100 to-gray-50 text-slate-gray text-xs sm:text-sm font-medium rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>
          
          <h3 className="text-xl sm:text-2xl font-bold text-carbon-black mb-2 group-hover:text-gradient transition-all duration-300">
            {project.name}
          </h3>
          <p className="text-electric-indigo font-medium mb-2 sm:mb-3 text-sm sm:text-base">
            {project.tagline}
          </p>
          <p className="text-slate-gray leading-relaxed text-sm sm:text-base">
            {project.description}
          </p>
          
          <a 
            href={project.url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center mt-4 sm:mt-6 text-fearless-blue font-semibold group-hover:text-electric-indigo transition-colors text-sm sm:text-base"
          >
            <span>Learn more</span>
            <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>
    </div>
  );
};

const OurWork = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState(null);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filteredProjects = projects.filter(project => {
    if (activeFilter === 'all' && !selectedIndustry && !selectedPlatform) {
      return true;
    }
    if (selectedIndustry) {
      const projectIndustries = Array.isArray(project.industry) ? project.industry : [project.industry];
      if (!projectIndustries.includes(selectedIndustry)) {
        return false;
      }
    }
    if (selectedPlatform && !project.platform.includes(selectedPlatform)) {
      return false;
    }
    return true;
  });

  const handleAllProjects = () => {
    setActiveFilter('all');
    setSelectedIndustry(null);
    setSelectedPlatform(null);
  };

  const handleIndustryFilter = (industry) => {
    setSelectedIndustry(selectedIndustry === industry ? null : industry);
    setSelectedPlatform(null);
  };

  const handlePlatformFilter = (platform) => {
    setSelectedPlatform(selectedPlatform === platform ? null : platform);
    setSelectedIndustry(null);
  };

  return (
    <div className="min-h-screen bg-cloud-white">
      <SEO 
        title="Our Work - Portfolio & Case Studies"
        description="See how Fearless IT has helped companies turn ideas into scalable, production-ready software. Explore our portfolio of custom web apps, mobile apps, SaaS platforms, and more."
        keywords="software portfolio, case studies, web development projects, mobile app development, SaaS platforms, custom software examples, client work"
        url="/our-work"
        image="https://static.prod-images.emergentagent.com/jobs/1c71d773-84d6-4251-9bae-60816a9265db/images/52437d580cf78ef64dcce62cd3051cba60889dc3bf8e8d6e4c20d4abd4d905b7.png"
      />
      
      {/* Navigation */}
      <MarketingHeader />

      {/* Hero Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-white to-cloud-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-carbon-black mb-4 sm:mb-6" data-testid="our-work-title">
              From Idea to
              <span className="text-gradient"> Impact</span>
            </h1>
            <p className="text-base sm:text-lg text-slate-gray leading-relaxed mb-8 sm:mb-12 px-4">
              We partner with founders and teams to design and develop custom digital products. See how our work has helped companies turn ideas into scalable, production-ready software.
            </p>

            {/* Filter Section */}
            <div className="space-y-4 sm:space-y-6 md:space-y-8">
              {/* All Projects Button */}
              <div>
                <button
                  onClick={handleAllProjects}
                  className={
                    activeFilter === 'all' && !selectedIndustry && !selectedPlatform
                      ? 'px-6 sm:px-8 py-2.5 sm:py-3 rounded-full font-semibold text-base sm:text-lg transition-all shadow-lg gradient-fearless text-white'
                      : 'filter-btn-lg'
                  }
                  data-testid="filter-all-projects"
                >
                  All Projects
                </button>
              </div>

              {/* Industry Filter */}
              <div className="flex items-center justify-center gap-2 sm:gap-4 flex-wrap px-4">
                <span className="text-carbon-black font-semibold text-sm sm:text-base">Industry:</span>
                {industries.map((industry) => (
                  <button
                    key={industry}
                    onClick={() => handleIndustryFilter(industry)}
                    className={
                      selectedIndustry === industry
                        ? 'px-4 sm:px-6 py-1.5 sm:py-2 rounded-full font-medium text-sm sm:text-base gradient-fearless text-white shadow-md'
                        : 'filter-btn text-sm sm:text-base'
                    }
                    data-testid={`filter-industry-${industry.toLowerCase()}`}
                  >
                    {industry}
                  </button>
                ))}
              </div>

              {/* Platform Filter */}
              <div className="flex items-center justify-center gap-2 sm:gap-4 flex-wrap px-4">
                <span className="text-carbon-black font-semibold text-sm sm:text-base">Platform:</span>
                {platforms.map((platform) => (
                  <button
                    key={platform}
                    onClick={() => handlePlatformFilter(platform)}
                    className={
                      selectedPlatform === platform
                        ? 'px-4 sm:px-6 py-1.5 sm:py-2 rounded-full font-medium text-sm sm:text-base gradient-fearless text-white shadow-md'
                        : 'filter-btn text-sm sm:text-base'
                    }
                    data-testid={`filter-platform-${platform.toLowerCase().replace(/\//g, '-')}`}
                  >
                    {platform}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-10 sm:py-16 pb-20 sm:pb-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-6 sm:gap-10">
            {filteredProjects.map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </div>
          {filteredProjects.length === 0 && (
            <div className="text-center py-16 sm:py-20">
              <p className="text-slate-gray text-base sm:text-lg">No projects found matching your filters.</p>
              <button
                onClick={handleAllProjects}
                className="mt-4 text-fearless-blue font-semibold hover:underline"
              >
                View all projects
              </button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24 bg-cloud-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-carbon-black mb-4 sm:mb-6">
            Ready to build something amazing?
          </h2>
          <p className="text-base sm:text-lg text-slate-gray mb-8 sm:mb-10 max-w-2xl mx-auto">
            Let's discuss your project and bring your vision to life with cutting-edge technology.
          </p>
          <Link to="/register">
            <button className="filter-btn-lg inline-flex items-center">
              Start Your Project
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <MarketingFooter />

      {/* CSS for gradient animation */}
      <style>{`
        @keyframes gradient-rotate {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
        
        .animate-gradient-shift {
          background-size: 200% 200%;
          animation: gradient-rotate 3s ease infinite;
        }
      `}</style>
    </div>
  );
};

export default OurWork;
